import { FC, useContext } from "react";
import Page from "@app/shared/Page";
import PageInner from "@app/shared/PageInner";
import PageHeader from "@app/shared/PageHeader";
import PageSection from "@app/shared/PageSection";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { Accordion } from "@app/shared/Accordion";
import ComplianceConnectNetworksTable from "@app/compliance-connect/regulator/ComplianceConnectNetworksTable";
import ComplianceConnectTotalWidget from "@app/compliance-connect/regulator/widgets/ComplianceConnectTotalWidget";
import ComplianceConnectOpenGapsWidget from "@app/compliance-connect/regulator/widgets/ComplianceConnectOpenGapsWidget";
import ComplianceConnectTopCountySpecialtyWidget from "@app/compliance-connect/regulator/widgets/ComplianceConnectTopCountySpecialtyWidget";
import ComplianceConnectIsolatedGapInstancesWidget from "@app/compliance-connect/regulator/widgets/ComplianceConnectIsolatedGapInstancesWidget";
import ComplianceConnectUniqueGapFillsWidget from "@app/compliance-connect/regulator/widgets/ComplianceConnectUniqueGapFillsWidget";
import ComplianceConnectFiltersContextProvider, { ComplianceConnectFiltersContext } from "@app/compliance-connect/regulator/contexts/ComplianceConnectFiltersContext";
import { ComplianceConnectFilterBar } from "@app/compliance-connect/common/ComplianceConnectFilterBar";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { getGenericErrorMessage } from "@app/shared/utility/getGenericErrorMessage";

const WrappedComplianceConnectDashboard: FC = () => {
    const filters = useContext(ComplianceConnectFiltersContext);

    return (
        <Page documentTitle="Compliance Connect">
            <Breadcrumbs template={BreadcrumbTemplateEnum.ComplianceConnect} items={[]} />
            <PageInner>
                <PageHeader title="Network Overview" disableHR={false} />
                <PageSection>
                    {filters.isFilterOptionsError ? (
                        <QESAlert type="danger" persist>{getGenericErrorMessage()}</QESAlert>
                    ) : (
                        <ComplianceConnectFilterBar
                            clearAllTestId="cc-filter-clear-all"
                            containerTestId="compliance-connect-filter-bar"
                            disabled={filters.isFilterOptionsLoading}
                            networkNameSearch={{
                                onChange: filters.setNetworkNameSearch,
                                testId: "cc-filter-network-name",
                                value: filters.networkNameSearch,
                            }}
                            onClearAll={filters.resetFilters}
                            onSearch={filters.applyFilters}
                            onServiceAreasChange={filters.setSelectedServiceAreas}
                            onSpecialtiesChange={filters.setSelectedSpecialties}
                            searchTestId="cc-filter-search"
                            selectedServiceAreas={filters.selectedServiceAreas}
                            selectedSpecialties={filters.selectedSpecialties}
                            serviceAreaOptions={filters.serviceAreaOptions}
                            serviceAreaTestId="cc-filter-service-area"
                            serviceAreaVariant="nested"
                            specialtyOptions={filters.specialtyOptions}
                            specialtyTestId="cc-filter-specialty"
                        />
                    )}
                </PageSection>
                <PageSection>
                    <AccordionContainer className="col-sm-12 px-0">
                        <Accordion defaultOpen={true} title="Key Overall Metrics" testId="compliance-connect-key-metrics__accordion">
                            <div className="d-flex flex-wrap" style={{ gap: '.5rem' }}>
                                <ComplianceConnectTotalWidget />
                                <ComplianceConnectOpenGapsWidget />
                                <ComplianceConnectTopCountySpecialtyWidget />
                                <ComplianceConnectIsolatedGapInstancesWidget />
                                <ComplianceConnectUniqueGapFillsWidget />
                            </div>
                        </Accordion>
                    </AccordionContainer>
                </PageSection>
                <PageSection>
                    <ComplianceConnectNetworksTable />
                </PageSection>
            </PageInner>
        </Page>
    );
};

const ComplianceConnectDashboard: FC = () => (
    <ComplianceConnectFiltersContextProvider>
        <WrappedComplianceConnectDashboard />
    </ComplianceConnectFiltersContextProvider>
);

export default ComplianceConnectDashboard;
